import React from 'react'
import Button from 'components/Button'
import { observer } from 'mobx-react'
import CheckoutViewModel from '../CheckoutViewModel'
import Icon from 'components/Icon'
import { useActivation } from 'hooks/mobx-hooks'
import styles from './Summary.sass'
import cx from 'classnames'
import JobCheckoutViewModel, {
  AppliedCouponViewModel,
  JobScopeItemViewModel,
} from '../JobCheckoutViewModel'
import Spinner from 'components/Spinner'
import Tooltip from 'components/Tooltip'
import { extractMessageFromError } from 'utils/error-util'
import { formatMoney } from 'utils/money-util'
import {
  Dropdown,
  DropdownItem,
  DropdownItemTitle,
  DropdownTitle,
} from 'components/Dropdown'
import { IconButton } from 'components/IconButton'
import { useHistory } from 'react-router'
import link from 'infra/link'

interface SummaryItemProps {
  item: JobScopeItemViewModel
}

const SummaryItem = observer(function SummaryItem({ item }: SummaryItemProps) {
  return (
    <div className={styles.item}>
      <div className={styles.itemInfo}>
        <div>{item.name}</div>
        <div>x {item.quantity}</div>
      </div>
    </div>
  )
})

const SummaryDivider = () => {
  return <div className={styles.divider} />
}

const Logo = () => {
  return (
    <div className={styles.logo}>
      <img
        src={require('assets/logos/logo_taxfyle-white.svg')}
        alt="White taxfyle logo."
      />
    </div>
  )
}

interface SummaryHeaderProps {
  vm: JobCheckoutViewModel
}

const SummaryHeader = observer(function SummaryHeader({
  vm,
}: SummaryHeaderProps) {
  return (
    <div className={styles.header}>
      <div className={styles.jobName}>{vm.job.displayName}</div>
      <div className={styles.more}>
        <SummaryDropdown vm={vm} />
      </div>
    </div>
  )
})

interface SummaryDropdownProps {
  vm: JobCheckoutViewModel
}

const SummaryDropdown = observer(function SummaryDropdown({
  vm,
}: SummaryDropdownProps) {
  const history = useHistory()

  return (
    <Dropdown
      autoDismiss
      button={
        <IconButton>
          <Icon name="more" color="white" />
        </IconButton>
      }
    >
      <DropdownTitle>Actions</DropdownTitle>

      {vm.canRemoveJob && (
        <DropdownItem onClick={() => vm.remove()}>
          <DropdownItemTitle>Remove Job</DropdownItemTitle>
        </DropdownItem>
      )}
      <DropdownItem onClick={() => history.push(link.job(`${vm.id}`))}>
        <DropdownItemTitle>View Job</DropdownItemTitle>
      </DropdownItem>
    </Dropdown>
  )
})

interface SummaryItemsProps {
  vm: JobCheckoutViewModel
}
const SummaryItems = observer(function SummaryItems({ vm }: SummaryItemsProps) {
  return (
    <div className={styles.items}>
      {vm.jobScopeItems.map((item) => (
        <SummaryItem key={item.id} item={item} />
      ))}
    </div>
  )
})

interface SummaryDetailsProps {
  label: string
  value?: string
}

const SummaryDetails = ({ label, value }: SummaryDetailsProps) => {
  return (
    <div className={styles.subtotal}>
      <div>{label}</div>
      <div>{value}</div>
    </div>
  )
}

interface CouponSummaryProps {
  vm: AppliedCouponViewModel
}

const CouponSummary = observer(function CouponSummary({
  vm,
}: CouponSummaryProps) {
  useActivation(vm)
  return (
    <div className={styles.couponSummary}>
      <div className={styles.appliedBadge}>
        <Icon size={12} name="tick" /> Applied
      </div>
      <div className={styles.couponDetails}>
        <div className={styles.couponName}>{vm.name}</div>
        <div className={styles.couponDescription}>{vm.description}</div>
      </div>
      <div className={styles.couponDiscount}>{vm.discount}</div>
      <Button type="translucent" borderless onClick={() => vm.removeCoupon()}>
        <span className={styles.removeCoupon}>
          <Icon size={24} name="close" />
        </span>
      </Button>
    </div>
  )
})

interface SummaryDiscountProps {
  vm: JobCheckoutViewModel
}

const SummaryDiscount = observer(function SummaryDiscount({
  vm,
}: SummaryDiscountProps) {
  if (!vm.canAddCoupons) {
    return null
  }

  return (
    <>
      {vm.hasCoupons ? (
        <>
          <SummaryDetails label="Discount coupon" value={vm.discountLabel} />
          {vm.appliedCouponVM && <CouponSummary vm={vm.appliedCouponVM} />}
        </>
      ) : (
        <Button
          type="translucent"
          borderless
          onClick={() => vm.openCouponDialog()}
        >
          <span className={styles.discount}>Apply discount coupon</span>
        </Button>
      )}
    </>
  )
})

interface JobSummaryItemProps {
  vm: JobCheckoutViewModel
}

const JobSummaryItem = observer(function JobSummaryItem({
  vm,
}: JobSummaryItemProps) {
  return (
    <div
      className={cx(
        styles.jobSummary,
        (vm.checkoutCompleted || !vm.canAddCoupons) && styles.spacious,
      )}
    >
      <SummaryHeader vm={vm} />
      <SummaryItems vm={vm} />
      <SummaryDivider />
      <SummaryDetails label="Subtotal" value={vm.primaryAmount} />
      <SummaryDivider />
      <SummaryDiscount vm={vm} />
      {vm.pay.match({
        pending: () => (
          <div className={styles.loadingContainer}>
            <Spinner size={24} />
          </div>
        ),
        rejected: (err) => (
          <div className={cx(styles.loadingContainer, styles.error)}>
            <Tooltip content={extractMessageFromError(err)} placement="top">
              <Icon size={22} name="declined" />
            </Tooltip>
          </div>
        ),
        resolved: () => (
          <div
            className={cx(
              styles.loadingContainer,
              vm.checkoutCompleted && styles.success,
            )}
          >
            {vm.checkoutCompleted && <Icon size={22} name="checkmark" />}
          </div>
        ),
      })}
    </div>
  )
})

interface SummaryProps {
  vm: CheckoutViewModel
}

export default observer(function Summary({ vm }: SummaryProps) {
  return (
    <div className={styles.root}>
      <div
        className={cx(
          styles.summaryContainer,
          vm.checkoutCompleted && styles.completed,
        )}
      >
        <div className={styles.pageTitle}>
          {vm.checkoutCompleted ? 'Summary' : 'Checkout'}
        </div>
        <div className={styles.itemList}>
          {vm.jobCheckoutVMs.map((vm) => (
            <JobSummaryItem key={vm?.job?.id} vm={vm} />
          ))}
          <div className={styles.scrollGradient} />
        </div>
        <div>
          <div className={styles.total}>
            <div>Amount due now!!</div>
            <div>{formatMoney(vm.total)}</div>
          </div>
          <SummaryDivider />
          <Logo />
        </div>
      </div>
    </div>
  )
})
